//#region header */
/**************************************************************************************************
//
//  Description: Tree to display the MT document data.
//
//  Copyright:    © 2025 - 2026 Idox Software Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//#region Version 1.0.0.0 changes
//    001   25.02.25 Sean Flook          GMSCM-1 Initial Revision.
//    002   05.02.26 Sean Flook          GMSCM-12 Changed required for the V2 of the API.
//    003   26.02.26 Joshua McCormick    GMSCM-3 Order of newly added items in configuration and related trees to top.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion header */

import React, { ChangeEvent, Fragment, SyntheticEvent, useEffect, useState } from "react";

import useConfigQueryStore from "../context/ConfigStore";
import useUserQueryStore from "../context/UserStore";

import MultiTenantConfigGetDto from "../dtos/multiTenantConfigDto";
import { getConfigDocumentKey } from "../utils/ConfigNormalization";

import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { Box, IconButton, InputAdornment, Menu, MenuItem, Stack, TextField, Tooltip, Typography } from "@mui/material";

import { ExpandMore, ChevronRight } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AppsIcon from "@mui/icons-material/Apps";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ClearIcon from "@mui/icons-material/Clear";
import StorageIcon from "@mui/icons-material/Storage";

import {
  adsBlueA,
  adsDarkRed,
  adsLightGreyB,
  adsPaleBlueA,
  adsRed,
  adsRed10,
  adsRed20,
} from "../utils/ADSColour";
import { useTheme } from "@mui/material/styles";
import {
  ActionIconStyle,
  ClearSearchIconStyle,
  dataFormStyle,
  filterToolbarStyle,
  FormInputStyle,
  menuItemStyle,
  menuStyle,
  toolbarStyle,
  tooltipStyle,
} from "../utils/ADSStyles";

interface DocumentTreeProps {
  data: MultiTenantConfigGetDto[];
  selectedItem: string | undefined;
  expanded: string[];
  haveErrors: boolean;
  errorIds: string[];
  onItemSelected: (id: string | undefined) => void;
  onAddItem: (type: string, id?: string | undefined) => void;
  onDeleteItem: (type: string, id: string) => void;
  onExpandedItemsChange: (nodeIds: string[]) => void;
}

const DocumentTree: React.FC<DocumentTreeProps> = ({
  data,
  selectedItem,
  expanded,
  haveErrors,
  errorIds,
  onItemSelected,
  onAddItem,
  onDeleteItem,
  onExpandedItemsChange,
}) => {
  const theme = useTheme();

  const currentUser = useUserQueryStore((s) => s.userQuery.currentUser);

  const clearFilter = useConfigQueryStore((s) => s.configQuery.clearFilter);
  const setClearFilter = useConfigQueryStore((s) => s.setClearFilter);

  const [expandAll, setExpandAll] = useState<string>("Expand all");
  const [filterBy, setFilterBy] = useState<string>("");
  const [filteredData, setFilteredData] = useState<MultiTenantConfigGetDto[]>(data);
  const [treeItemErrorIds, setTreeItemErrorIds] = useState<string[]>([]);

  const [hoverId, setHoverId] = useState<string | undefined>(undefined);
  const hoveredConfigId = hoverId?.split("|")[0];

  const isHoverWithin = (itemId: string): boolean => {
    if (!hoverId) return false;
    return hoverId === itemId || hoverId.startsWith(`${itemId}|`);
  };

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  /**
   * Event to handle when the expand all button is clicked.
   */
  const handleExpandAll = () => {
    const expandList: string[] = [];
    data.forEach((doc) => {
      const docKey = getConfigDocumentKey(doc);
      expandList.push(docKey);
      (doc.organisations ?? []).forEach((org) => {
        expandList.push(`${docKey}|${org.organisationName}`);
        if (org.namedConnectionSettings?.length > 0) {
          org.namedConnectionSettings.forEach((connectionSetting) => {
            expandList.push(`${docKey}|${org.organisationName}|${connectionSetting.name}`);
          });
        }
        // Elastic settings are edited on the organisation form, not listed in the tree.
      });
    });

    if (onExpandedItemsChange) onExpandedItemsChange(expandAll === "Expand all" ? expandList : []);
    setExpandAll((oldExpandAll) => (oldExpandAll === "Expand all" ? "Collapse all" : "Expand all"));
  };

  /**
   * Event to handle when the filter changes.
   *
   * @param newValue The new value to filter by.
   */
  const handleFilterChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value;
    setFilterBy(newValue);
    if (newValue.length > 0) {
      const filteredList: MultiTenantConfigGetDto[] = data.filter((doc) =>
        (doc.name ?? "").toLowerCase().includes(newValue.toLowerCase()) ||
        (doc.appName ?? "").toLowerCase().includes(newValue.toLowerCase())
      );
      setFilteredData(filteredList);
      const foundCurrent = filteredList.find((doc) => getConfigDocumentKey(doc) === selectedItem);
      if (!foundCurrent && onItemSelected) onItemSelected(undefined);
    } else {
      setFilteredData(data);
    }
  };

  /**
   * Event to handle when the clear search button is clicked.
   */
  const handleClearSearch = (): void => {
    setFilterBy("");
    setFilteredData(data);
  };

  /**
   * Event to handle when the configuration menu is clicked.
   *
   * @param event The event object.
   */
  const handleConfigurationMenuClick = (event: SyntheticEvent<HTMLElement | null>) => {
    setAnchorEl(event.nativeEvent.target as HTMLElement);
  };

  /**
   * Event to handle when the configuration menu is closed.
   *
   * @param event The event object.
   */
  const handleConfigurationMenuClose = (event: SyntheticEvent) => {
    setAnchorEl(null);
    event.stopPropagation();
  };

  /**
   * Event to handle when the add configuration menu item is clicked.
   *
   * @param event The event object.
   */
  const handleAddConfigurationClick = (event: SyntheticEvent): void => {
    handleConfigurationMenuClose(event);
    if (onAddItem) onAddItem("configuration");
  };

  /**
   * Event to handle when the add organisation menu item is clicked.
   *
   * @param event The event object.
   * */
  const handleAddOrganisationClick = (event: SyntheticEvent): void => {
    handleConfigurationMenuClose(event);
    if (onAddItem) onAddItem("organisation", hoverId ?? "");
  };

  /**
   * Event to handle when the delete organisation menu item is clicked.
   *
   * @param event The event object.
   */
  const handleDeleteOrganisationClick = (event: SyntheticEvent): void => {
    handleConfigurationMenuClose(event);
    if (onDeleteItem) onDeleteItem("organisation", hoverId ?? "");
  };

  const handleAddConnectionSettingClick = (event: SyntheticEvent): void => {
    handleConfigurationMenuClose(event);
    if (onAddItem) onAddItem("connectionSetting", hoverId ?? "");
  };

  /**
   * Event to handle when a node is selected.
   *
   * @param {object} event The event object.
   * @param {number} nodeId The id of the selected node.
   */
  function handleNodeSelected(event: SyntheticEvent, itemIds: string | null) {
    event.stopPropagation();
    if (onItemSelected) onItemSelected(itemIds ?? undefined);
  }

  /**
   * Event to handle the toggling of the nodes.
   *
   * @param {object} event The event object.
   * @param {array} nodeIds The list of node ids that are expanded.
   */
  function handleNodeToggle(event: SyntheticEvent, nodeIds: string[]): void {
    event.stopPropagation();
    const iconClicked = (event.target as HTMLElement).closest(".MuiTreeItem-iconContainer");
    if (iconClicked && onExpandedItemsChange) {
      onExpandedItemsChange(nodeIds);
    }
  }

  /**
   * Event to handle when the mouse enters a configuration item.
   *
   * @param {string} recId
   */
  const handleMouseEnterTreeItem = (recId: string): void => {
    setHoverId(recId);
  };

  /**
   * Event to handle when the mouse leaves a configuration item.
   */
  const handleMouseLeaveTreeItem = (): void => {
  };

  /**
   * Event to handle when the mouse leaves the tree container.
   */
  const handleMouseLeaveTree = (): void => {
    setHoverId(undefined);
  };

  /**
   * Method to get styling used for a tree item, based on whether usrn is current street and/or checked.
   *
   * @param {string} id The id for the document
   * @returns {object} The styling for the tree item.
   */
  const treeItemStyle = (id: string, hasError: boolean): object => {
    const currentDocument = id === selectedItem;
    if (hasError)
      return {
        backgroundColor: adsRed10,
        color: adsRed,
        borderTop: `solid ${adsLightGreyB} 1px`,
        pb: theme.spacing(1),
        pt: theme.spacing(1),
        "&:hover": {
          backgroundColor: adsRed20,
          color: adsDarkRed,
        },
      };
    else if (currentDocument)
      return {
        backgroundColor: adsPaleBlueA,
        borderTop: `solid ${adsLightGreyB} 1px`,
        pb: theme.spacing(1),
        pt: theme.spacing(1),
        "&:hover": {
          backgroundColor: adsPaleBlueA,
          color: adsBlueA,
        },
      };
    else
      return {
        backgroundColor: "inherit",
        borderTop: `solid ${adsLightGreyB} 1px`,
        pb: theme.spacing(1),
        pt: theme.spacing(1),
        "&:hover": {
          backgroundColor: adsPaleBlueA,
          color: adsBlueA,
        },
      };
  };

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  useEffect(() => {
    if (clearFilter) {
      setFilterBy("");
      setFilteredData(data);
      setClearFilter(false);
    }
  }, [clearFilter, data, setClearFilter]);

  useEffect(() => {
    if (!haveErrors) {
      setTreeItemErrorIds([]);
    } else {
      const ids: string[] = [];
      let errorIdParts: string[];
      errorIds.forEach((errorId) => {
        if (!errorId) return;
        errorIdParts = errorId.split("|");
        errorIdParts.forEach((part, index) => {
          const idToAdd = errorIdParts.slice(0, index + 1).join("|");
          if (!ids.includes(idToAdd)) {
            ids.push(idToAdd);
          }
        });
      });
      setTreeItemErrorIds(ids);
    }
  }, [haveErrors, errorIds]);

  return (
    <Fragment>
      <Box sx={toolbarStyle} id="ads-document-tree-toolbar">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pl: "12px", pr: "12px", pt: "4px" }}
        >
          <Typography variant="body2">{`${
            data.length === filteredData.length
              ? data.length
              : filteredData.length.toString() + " of " + data.length.toString()
          } multi-tenant configurations`}</Typography>
          <Stack direction="row" alignItems="center" justifyContent={"flex-end"} spacing={1}>
            <Tooltip title={`${expandAll} items in list`} arrow placement="right" sx={tooltipStyle}>
              <IconButton onClick={handleExpandAll} sx={ActionIconStyle()} aria-controls="expand-collapse" size="small">
                {expandAll === "Expand all" ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                <Typography variant="body2">{expandAll}</Typography>
              </IconButton>
            </Tooltip>
            <Tooltip title="Actions" arrow placement="right" sx={tooltipStyle}>
              <IconButton
                onClick={handleConfigurationMenuClick}
                aria-controls="configuration_action-menu"
                aria-haspopup="true"
                size="small"
              >
                <MoreVertIcon sx={ActionIconStyle()} />
              </IconButton>
            </Tooltip>
            <Menu
              id="configuration_action-menu"
              elevation={2}
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleConfigurationMenuClose}
              sx={menuStyle}
            >
              {currentUser?.isSuperAdministrator && (
                <MenuItem dense onClick={handleAddConfigurationClick} sx={menuItemStyle(false)}>
                  <Typography variant="inherit">Add configuration</Typography>
                </MenuItem>
              )}
            </Menu>
          </Stack>
        </Stack>
      </Box>
      <Box sx={filterToolbarStyle} id="ads-document-tree-filter-toolbar">
        <TextField
          id="configuration-filter"
          autoComplete="off"
          label="Filter by configuration name"
          sx={FormInputStyle(false)}
          variant="outlined"
          fullWidth
          margin="dense"
          size="small"
          value={filterBy}
          onChange={handleFilterChange}
          placeholder="Filter by configuration name"
          slotProps={{
            htmlInput: {
              autoComplete: "off",
            },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton id="btnClear" onClick={handleClearSearch} aria-label="Clear filter" size="small">
                    <ClearIcon sx={ClearSearchIconStyle(filterBy)} />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <Box sx={dataFormStyle("DocumentTree")} onMouseLeave={handleMouseLeaveTree}>
        <SimpleTreeView
          aria-label="document tree"
          id="document-tree"
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            "& .MuiTreeItem-content": {
              borderRadius: 0,
              margin: 0,
              backgroundColor: "transparent !important",
              boxShadow: "none !important",
            },
            "& .MuiTreeItem-content:hover": {
              backgroundColor: "transparent !important",
            },
            "& .MuiTreeItem-content.Mui-focused, & .MuiTreeItem-content.Mui-selected, & .MuiTreeItem-content.Mui-selected:hover":
              {
                backgroundColor: "transparent !important",
                boxShadow: "none !important",
              },
            "& .MuiTreeItem-label": {
              backgroundColor: "transparent !important",
            },
          }}
          slots={{ expandIcon: ChevronRight, collapseIcon: ExpandMore }}
          expandedItems={expanded}
          selectedItems={selectedItem ?? null}
          onSelectedItemsChange={handleNodeSelected}
          onExpandedItemsChange={handleNodeToggle}
        >
          {[...filteredData]
            .sort((a, b) => {
              const aCreated = a.created ? new Date(a.created).getTime() : 0;
              const bCreated = b.created ? new Date(b.created).getTime() : 0;

              if (aCreated !== bCreated) return bCreated - aCreated;

              const aName = a.appName ?? "";
              const bName = b.appName ?? "";

              return aName.localeCompare(bName, undefined, { numeric: true, sensitivity: "base" });
            })
            .map((rec, index) => {
              const docKey = getConfigDocumentKey(rec);
              return (
                <TreeItem
                  key={`document-${docKey}-${index}`}
                  id={docKey}
                  itemId={docKey}
                  sx={treeItemStyle(docKey, haveErrors && treeItemErrorIds.includes(docKey))}
                  label={
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Stack direction="column" alignItems="flex-start" justifyContent="flex-start" spacing={1}>
                        <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1}>
                          <AppsIcon />
                          <Typography variant="subtitle2">{rec.name || rec.appName}</Typography>
                        </Stack>
                      </Stack>
                      <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ width: 32 }}>
                        <IconButton
                          onClick={handleAddOrganisationClick}
                          size="small"
                          title="Add organisation"
                          aria-label="Add organisation"
                          sx={{
                            visibility: docKey === hoveredConfigId ? "visible" : "hidden",
                          }}
                        >
                          <AddIcon sx={ActionIconStyle()} />
                        </IconButton>
                      </Stack>
                    </Stack>
                  }
                  onMouseEnter={() => handleMouseEnterTreeItem(docKey)}
                  onMouseLeave={handleMouseLeaveTreeItem}
                >
                  {(rec.organisations ?? []).map((recOrg, orgIndex) => {
                    return (
                      <TreeItem
                        key={`document-${docKey}-${index}-organisation-${recOrg.organisationName}-${orgIndex}`}
                        id={`${docKey}|${recOrg.organisationName}`}
                        itemId={`${docKey}|${recOrg.organisationName}`}
                        sx={treeItemStyle(
                          `${docKey}|${recOrg.organisationName}`,
                          haveErrors && treeItemErrorIds.includes(`${docKey}|${recOrg.organisationName}`)
                        )}
                        label={
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                            <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1}>
                              <CorporateFareIcon />
                              <Typography variant="caption">{recOrg.organisationName}</Typography>
                            </Stack>
                            {isHoverWithin(`${docKey}|${recOrg.organisationName}`) && (
                              <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1}>
                                <IconButton
                                  onClick={handleDeleteOrganisationClick}
                                  size="small"
                                  title="Delete organisation"
                                  aria-label="Delete organisation"
                                >
                                  <DeleteIcon sx={ActionIconStyle()} />
                                </IconButton>
                                <IconButton
                                  onClick={handleAddConnectionSettingClick}
                                  size="small"
                                  title="Add connection setting"
                                  aria-label="Add connection setting"
                                >
                                  <StorageIcon sx={ActionIconStyle()} fontSize="small" />
                                </IconButton>
                              </Stack>
                            )}
                          </Stack>
                        }
                        onMouseEnter={() => handleMouseEnterTreeItem(`${docKey}|${recOrg.organisationName}`)}
                        onMouseLeave={handleMouseLeaveTreeItem}
                      >
                        {(recOrg.namedConnectionSettings ?? []).map((recConn, connIndex) => {
                          return (
                            <TreeItem
                              key={`document-${docKey}-${index}-organisation-${recOrg.organisationName}-${orgIndex}-connection-${recConn.name}-${connIndex}`}
                            id={`${docKey}|${recOrg.organisationName}|${recConn.name}`}
                              itemId={`${docKey}|${recOrg.organisationName}|${recConn.name}`}
                              sx={treeItemStyle(
                                `${docKey}|${recOrg.organisationName}|${recConn.name}`,
                                haveErrors &&
                                  treeItemErrorIds.includes(`${docKey}|${recOrg.organisationName}|${recConn.name}`)
                              )}
                              label={
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                  <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1}>
                                    <StorageIcon />
                                    <Typography variant="caption">{recConn.name}</Typography>
                                  </Stack>
                                </Stack>
                              }
                              onMouseEnter={() =>
                                handleMouseEnterTreeItem(`${docKey}|${recOrg.organisationName}|${recConn.name}`)
                              }
                              onMouseLeave={handleMouseLeaveTreeItem}
                            />
                          );
                        })}
                      </TreeItem>
                    );
                  })}
                </TreeItem>
              );
            })}
        </SimpleTreeView>
      </Box>
    </Fragment>
  );
};

export default DocumentTree;
